import { useEffect, useRef } from "react";
import { map } from "../../mapbox/map.sideEffects/useMap.sideEffects.js";
import {
  getCurrentCampusCamera,
  stableEaseTo,
} from "../../mapbox/functions/interactivity.map.js";
import {
  initialZoom,
  zoomLowerCampus,
} from "../../mapbox/functions/filter.map.js";
import { fastSlowFast } from "../../../../functions/helper/animationEasing.functions.js";
import { POI_NODES } from "../../../../reduxStore/actions/main.js";
import { getNodeById } from "../../../../functions/classes/node.class.js";
import {
  terminalIdleRotation,
  terminalPitch,
  terminalZoom,
} from "../../../../app/app.sideEffects/useURLParams.sideEffects.js";
import { MISC_INFO } from "../../../../reduxStore/actions/main.js";

/**
 * Setzt beim Aufruf von MapInfo die KameraRichtig.
 * Öffnet oder schließt den Etagenwechsler, je nach Zoomlevel
 * @param props
 */
export function useCameraView(props) {
  // --- Refs
  const initialViewIsSet = useRef(false);
  const terminalIsIdle_ref = useRef(props.terminalIsIdle);
  const terminalIsIdle_prev = useRef(false);
  const startRotation = useRef(0);

  // --- Funktionen
  const setStandardBearing = () => {
    // Um dragRotate nach einer Route wieder in den  Standard zu setzen
    if (props.defaultAngle != null) {
      map.setBearing(props.defaultAngle);
    }
  };
  const setCameraForCampus = () => {
    const campusCamera = getCurrentCampusCamera();

    if (campusCamera != null) {
      if (campusCamera.zoom < zoomLowerCampus + 1) {
        campusCamera.zoom = zoomLowerCampus + 0.1;
      }

      campusCamera.duration = 4500;
      if (props.isTerminal && props.terminalBearing != null) {
        campusCamera.bearing = props.terminalBearing;
      }
      if (props.isTerminal && terminalZoom != null) {
        campusCamera.zoom = terminalZoom;
      }
      if (props.isTerminal && terminalPitch != null) {
        campusCamera.pitch = terminalPitch;
      }

      campusCamera.easing = fastSlowFast;

      if (MISC_INFO.startZoomLevel != null) {
        campusCamera.zoom = MISC_INFO.startZoomLevel;
      }

      stableEaseTo(campusCamera);
    } else {
      stableEaseTo({ zoom: initialZoom });
    }
    initialViewIsSet.current = true;
  };
  const setCameraToTerminal = () => {
    const node = getNodeById(props.defaultStartNode, POI_NODES);
    props.setCurrentFeature();

    if (node) {
      props.setCurrentFloorNumber(node.properties.level);
      stableEaseTo({
        bearing: props.terminalBearing,
        center: node.geometry.coordinates,
        zoom: terminalZoom,
        pitch: terminalPitch,
        duration: 2000,
      });
    } else {
      props.setCurrentFloorNumber(0);
      setCameraForCampus();
      console.warn(
        `defaultStartNode mit ID ${props.defaultStartNode} nicht gefunden. Kann nicht auf einen punkt zeigen!`
      );
    }
  };
  const rotateCameraAroundCampus = (timestamp) => {
    if (!terminalIsIdle_ref.current) return;

    if (startRotation.current === 0 && timestamp > 0) {
      startRotation.current = timestamp - map.getBearing();
    }
    const elapsed = timestamp - startRotation.current;

    // clamp the rotation between 0 -360 degrees
    // Divide timestamp by 100 to slow rotation to ~10 degrees / sec
    const newRotation = (elapsed / 360) % 360;
    map.rotateTo(newRotation, {
      duration: 0,
    });
    // Request the next frame of the animation.
    requestAnimationFrame(rotateCameraAroundCampus);
  };
  const startRotateCameraAroundCampus = () => {
    // erst checken ob sich die map bewegt
    // wenn ja abwarten, weil das ist dann die bewegung zum campus hin
    if (map.isMoving()) {
      map.once("moveend", () => {
        // sobald die bewegung fertig, rotiere um den campus herum!
        rotateCameraAroundCampus(0);
      });
    } else {
      rotateCameraAroundCampus(0);
    }
  };
  const cancelRotateCameraAroundCampus = () => {
    startRotation.current = 0;
  };

  // --- Effect-Functions
  const setInitCamera = () => {
    if (!props.mapLoaded) return;

    if (
      props.currentCampusId != null &&
      !props.currentFeature &&
      !props.isTerminal
    ) {
      setCameraForCampus();
    } else if (props.isTerminal) {
      setCameraToTerminal();
    }
    setStandardBearing();
  };
  const startIdleCamera = () => {
    if (!props.isTerminal || !props.mapLoaded) return;
    // steck den idle state in ein Ref objekt, weil der state allein leider in der rotation funktion
    // nicht referenziert wird und die funktion daher sonst nie mitbekommt, wenn sich
    // das state ändert!
    terminalIsIdle_ref.current = props.terminalIsIdle;

    if (props.terminalBearing == null) {
      if (props.terminalIsIdle && terminalIdleRotation) {
        startRotateCameraAroundCampus();
      } else {
        cancelRotateCameraAroundCampus();
      }
    } else {
      setCameraToTerminal();
    }
  };

  // --- Effects
  // Kamera bei Aufruf von MapInfo Component
  useEffect(setInitCamera, [props.mapLoaded, props.terminalIsIdle]);

  // Kamera bei Terminal im Idle Zustand zur rotation führen
  useEffect(startIdleCamera, [
    props.mapLoaded,
    props.currentCampusId,
    props.isTerminal,
    props.terminalIsIdle,
  ]);

  useEffect(() => {
    terminalIsIdle_prev.current = props.terminalIsIdle;
  }, [props.terminalIsIdle]);
}
